import { useEffect, useState } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    FieldWrappers,
    //Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    RupeeIcon,
    PrimaryAction,
    ReplicateContent,
    ReplicateHeader,
    ReplicateSection,
    ReplicateTitle,
    ReplicateSubtitle,
    ReplicateSelectionCard,
    ModalHeader,
} from "../Styles/InputStyles";
import { Select, MenuItem, FormControl, CircularProgress } from "@mui/material";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "../SinglePropSelectModal";
import MultiPropSelectModal from "../MultiPropSelectModal";

const validationSchema = Yup.object({
    incrementType: Yup.number()
        .required("Increment type is required"),

    incrementValue: Yup.number()
        .typeError("Increment value must be a number")
        .required("Increment value is required")
        .when('incrementType', {
            is: 1,
            then: (schema) => schema
                .min(1, "Flat amount cannot be negative or 0")
                .max(100000, "Flat amount cannot exceed ₹1,00,000"),
            otherwise: (schema) => schema
                .min(1, "Percentage cannot be negative or 0")
                .max(100, "Percentage cannot exceed 100%")
        }),

    increaseSecurity: Yup.boolean(),

    agreementCharges: Yup.number()
        .typeError("Agreement Charges must be a number")
        .nullable()
        .transform((value, originalValue) =>
            originalValue === '' ? null : value
        )
        .test(
            'agreement-charges-range',
            'Agreement Charges must be between ₹1 and ₹5,00,000',
            function (value) {
                if (value === null || value === undefined || value === '') {
                    return true;
                }
                return value >= 1 && value <= 500000;
            }
        ),

    replicate: Yup.boolean(),
    autoRenew: Yup.boolean(),
});

const AgreementRenew = ({
    onClose,
    settingsData,
    property,
    propertyList,
    editAgreementDetailMutate,
    editAgreementDetailLoading,
    rentingDetailsMutate,
    isLoading,
    propertyParam,
    setIsEditing,
    isEditing,
    selectedProperties,
    setSelectedProperties
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState();
    const [selectedPropertyId, setSelectedPropertyId] = useState(propertyParam);
    // const [selectedProperties, setSelectedProperties] = useState([]);
    const [isPropertyChanging, setIsPropertyChanging] = useState(false);
    const [isReplicating, setIsReplicating] = useState(false);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const allProperties = propertyList || [];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    useEffect(() => {
        if (propertyParam && filteredProperties.length > 0) {
            const propertyFromParam = filteredProperties.find(prop => prop.id === propertyParam);
            if (propertyFromParam && propertyFromParam.id !== selectedPropertyId) {
                setSelectedProperty(propertyFromParam);
                setSelectedPropertyId(propertyFromParam.id);
            }
        }
    }, [propertyParam, filteredProperties]); // Remove selectedPropertyId from dependencies

    useEffect(() => {
        if (propertyList) {
            const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
            setFilteredProperties(filtered);

            // Only set initial property if no property is selected AND we have a propertyParam
            if (filtered.length > 0 && !selectedProperty) {
                let initialProperty;

                if (propertyParam && propertyParam !== 0) {
                    initialProperty = filtered.find(prop => prop.id === propertyParam);
                }

                if (!initialProperty) {
                    initialProperty = filtered[0];
                }

                setSelectedProperty(initialProperty);
                setSelectedPropertyId(initialProperty.id);
            }
        }
    }, [propertyList, propertyParam]);

    useEffect(() => {
        if (propertyParam && propertyParam !== selectedPropertyId) {
            setSelectedPropertyId(propertyParam);

            if (filteredProperties.length > 0) {
                const propertyFromParam = filteredProperties.find(prop => prop.id === propertyParam);
                if (propertyFromParam) {
                    setSelectedProperty(propertyFromParam);
                }
            }
        }
    }, [propertyParam, filteredProperties]);


    const getCurrentSettings = () => {
        return {
            incrementType: settingsData?.rentIncrementType || '',
            incrementValue: settingsData?.rentIncrementValue || '',
            increaseSecurity: settingsData?.updateSecurity === 1,
            agreementCharges: settingsData?.agreementCharges || "",
            autoRenew: settingsData?.autoRenew === 1,
        };
    };

    const currentSettings = getCurrentSettings();

    const handleSubmit = (values) => {
        const submitData = {
            rentIncrementType: values.incrementType,
            rentIncrement: values.incrementValue,
            updateSecurity: values.increaseSecurity ? 1 : 0,
            agreementCharges: values.agreementCharges || 0,
            autoRenew: values.autoRenew ? 1 : 0,
            selectedProperties: [selectedPropertyId],
            selectedPropertyId: selectedPropertyId,
        };
        editAgreementDetailMutate(submitData);
    };

    const handleReplicateSettings = () => {
        if (selectedProperties.length === 0) {
            setShowMultiSelectModal(true);
            return;
        }

        setIsReplicating(true);

        const submitData = {
            rentIncrementType: currentSettings.incrementType,
            rentIncrement: currentSettings.incrementValue,
            updateSecurity: currentSettings.increaseSecurity ? 1 : 0,
            agreementCharges: currentSettings.agreementCharges || 0,
            autoRenew: currentSettings.autoRenew ? 1 : 0,
            selectedProperties: selectedProperties,
            replicate: 1,
            selectedPropertyId: selectedPropertyId,
        };

        editAgreementDetailMutate(submitData, {
            onSuccess: () => {
                setIsReplicating(false);
                setSelectedProperties([]);
            },
            onError: () => {
                setIsReplicating(false);
            }
        });
    };

    const handleSelectedProperty = (selectedOption) => {
        const newPropertyId = selectedOption.id;

        if (newPropertyId === selectedPropertyId) {
            return;
        }

        setIsPropertyChanging(true);
        setSelectedProperty(selectedOption);
        setSelectedPropertyId(newPropertyId);

        if (rentingDetailsMutate && newPropertyId) {
            rentingDetailsMutate(newPropertyId, {
                onSettled: () => {
                    setIsPropertyChanging(false);
                }
            });
        } else {
            setIsPropertyChanging(false);
        }
    };
    const handleMultiPropertySave = (selectedProps) => {
        setSelectedProperties(selectedProps);
        setShowMultiSelectModal(false);
    };

    const renderSelectedProperties = (selected = []) => {
        if (!selected.length) return "No properties selected";

        if (selected.length === allProperties.length) {
            return "All Properties";
        }

        const selectedPropertyNames = allProperties
            .filter((prop) => selected.includes(prop.id))
            .map((prop) => prop.name);

        if (!selectedPropertyNames.length) return "No properties selected";

        const maxToShow = 2;
        const namesToShow = selectedPropertyNames.slice(0, maxToShow);
        const remainingCount = selectedPropertyNames.length - maxToShow;

        return (
            <PropertyDisplayContainer>
                {namesToShow.join(", ")}
                {remainingCount > 0 && (
                    <RemainingCount>+{remainingCount}</RemainingCount>
                )}
            </PropertyDisplayContainer>
        );
    };

    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ModalHeader>
                <HeaderTitle>{isEditing ? "Edit Renew Agreement" : "Renew Agreement"}</HeaderTitle>
                <CloseButton onClick={onClose}>
                    <MdClose color="white" size={24} />
                </CloseButton>
            </ModalHeader>

            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <CircularProgress />
                </div>
            ) : (
                <ContentContainer>
                    <div className={isEditing ? "px-6 py-0" : "px-6 pt-6 pb-20"}>
                        <Section>
                            {!isEditing && property?.id === 0 && filteredProperties.length > 0 && (
                                // <PropertySection>
                                <CategoryCard
                                    title="Property"
                                    selectedCategory={selectedProperty}
                                    onClick={() => setShowPropertyModal(true)}
                                />
                                // </PropertySection>
                            )}

                            {isEditing ? (
                                <Formik
                                    initialValues={currentSettings}
                                    onSubmit={(values) => {
                                        handleSubmit(values);
                                    }}
                                    validationSchema={validationSchema}
                                    enableReinitialize
                                >
                                    {({ values, setFieldValue, errors, touched, handleChange, handleBlur }) => {
                                        const handleIncrementTypeChange = (value) => {
                                            setFieldValue('incrementType', value);
                                            setFieldValue('incrementValue', '');
                                            setFieldValue('increaseSecurity', false);
                                        };

                                        return (
                                            <Form>
                                                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                                    <Wrapper>
                                                        <InputGroup>
                                                            <Label htmlFor="incrementType">Increment Type *</Label>
                                                            <FormControl fullWidth>
                                                                <Select
                                                                    id="incrementType"
                                                                    name="incrementType"
                                                                    value={values.incrementType || ""}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        if (value === "") {
                                                                            setFieldValue("incrementType", "");
                                                                            handleIncrementTypeChange(value);
                                                                        } else {
                                                                            handleIncrementTypeChange(value);
                                                                        }
                                                                    }}
                                                                    displayEmpty
                                                                    style={{ height: "46px" }}
                                                                    className="px-2"
                                                                    error={touched.incrementType && Boolean(errors.incrementType)}
                                                                    renderValue={(selected) => {
                                                                        if (selected === "") {
                                                                            return <span className="text-gray-400">Select increment type</span>;
                                                                        }
                                                                        return selected === 1
                                                                            ? "Flat Amount (₹)"
                                                                            : "Percentage (%)";
                                                                    }}
                                                                >
                                                                    <MenuItem value="">
                                                                        <em className="text-gray-400">Select increment type</em>
                                                                    </MenuItem>
                                                                    <MenuItem value={1}>Flat Amount (₹)</MenuItem>
                                                                    <MenuItem value={2}>Percentage (%)</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            {errors.incrementType && touched.incrementType && (
                                                                <ErrorMessage>{errors.incrementType}</ErrorMessage>
                                                            )}
                                                        </InputGroup>

                                                        {values.incrementType && (
                                                            <InputGroup>
                                                                <Label htmlFor="incrementValue">
                                                                    {values.incrementType === 1 ? 'Flat Amount *' : 'Percentage *'}
                                                                </Label>
                                                                <FieldWrappers>
                                                                    {values.incrementType === 1 && <RupeeIcon />}
                                                                    <Field
                                                                        type="number"
                                                                        name="incrementValue"
                                                                        id="incrementValue"
                                                                        placeholder={values.incrementType === 1 ? "Enter amount" : "Enter % increase"}
                                                                        value={values.incrementValue}
                                                                        className={values.incrementType === 1 ? "pl-6" : ""}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                    {values.incrementType === 2 && (
                                                                        <span className="absolute right-3 text-gray-500">%</span>
                                                                    )}
                                                                </FieldWrappers>
                                                                {errors.incrementValue && touched.incrementValue && (
                                                                    <ErrorMessage>{errors.incrementValue}</ErrorMessage>
                                                                )}
                                                            </InputGroup>
                                                        )}

                                                        {values.incrementType && values.incrementValue && (
                                                            <InputGroup>
                                                                <div className="flex items-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        name="increaseSecurity"
                                                                        id="increaseSecurity"
                                                                        checked={values.increaseSecurity}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        className="mr-2 h-4 w-4 accent-primary-color"
                                                                    />
                                                                    <Label htmlFor="increaseSecurity" className="mb-0 cursor-pointer">
                                                                        Apply same increment amount to security deposit
                                                                    </Label>
                                                                </div>
                                                            </InputGroup>
                                                        )}

                                                        <InputGroup>
                                                            <Label htmlFor="agreementCharges">Agreement Charges</Label>
                                                            <FieldWrappers>
                                                                <RupeeIcon />
                                                                <Field
                                                                    type="number"
                                                                    name="agreementCharges"
                                                                    id="agreementCharges"
                                                                    placeholder="Enter agreement charges"
                                                                    value={values.agreementCharges}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    className="pl-6"
                                                                />
                                                            </FieldWrappers>
                                                            {errors.agreementCharges && touched.agreementCharges && (
                                                                <ErrorMessage>{errors.agreementCharges}</ErrorMessage>
                                                            )}
                                                        </InputGroup>
                                                        {/* <InputGroup>
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    name="autoRenew"
                                                                    id="autoRenew"
                                                                    checked={values.autoRenew}
                                                                    onChange={handleChange}
                                                                    className="mr-2 h-4 w-4"
                                                                />
                                                                <Label htmlFor="autoRenew" className="mb-0 cursor-pointer">
                                                                    Enable Auto Renew Agreement
                                                                </Label>
                                                            </div>
                                                        </InputGroup> */}
                                                        <InputGroup>

                                                            <div className="flex items-start gap-3">

                                                                <input
                                                                    type="checkbox"
                                                                    name="autoRenew"
                                                                    id="autoRenew"
                                                                    checked={values.autoRenew}
                                                                    onChange={handleChange}
                                                                    className="mt-1 h-4 w-4"
                                                                />

                                                                <div className="flex flex-col">

                                                                    <Label htmlFor="autoRenew" className="mb-0 cursor-pointer font-medium text-gray-800">
                                                                        Auto-Renew Agreement
                                                                    </Label>

                                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                                                        Automatically renew the tenant’s agreement when it expires.
                                                                    </p>

                                                                    {/* WHAT WILL HAPPEN */}
                                                                    <ImpactBox>
                                                                        <ul className="list-disc pl-4 space-y-1">
                                                                            <li>Agreement duration will reset for the next cycle</li>
                                                                            <li>Rent will be updated based on the increment settings above</li>
                                                                            <li>No manual renewal action will be required</li>
                                                                        </ul>
                                                                    </ImpactBox>

                                                                    {/* WHEN DISABLED */}
                                                                    {!values.autoRenew && (
                                                                        <HelperText>
                                                                            You will need to manually renew agreements after expiry.
                                                                        </HelperText>
                                                                    )}

                                                                </div>

                                                            </div>

                                                        </InputGroup>
                                                    </Wrapper>
                                                </ScrollableContent>
                                                <ActionSection $isEditing={isEditing}>
                                                    <SideCancelBtn
                                                        type="button"
                                                        $isEditing={isEditing}
                                                        onClick={() => setIsEditing(false)}
                                                    >
                                                        Back
                                                    </SideCancelBtn>
                                                    <SubmitBtn $isEditing={isEditing} type="submit" disabled={editAgreementDetailLoading}>
                                                        {editAgreementDetailLoading ? "Updating..." : "Update"}
                                                    </SubmitBtn>
                                                </ActionSection>
                                            </Form>
                                        );
                                    }}
                                </Formik>
                            ) : (
                                <>
                                    {isPropertyChanging ? (
                                        <div className="flex items-center justify-center h-full pt-10">
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <>
                                            <SettingsGrid>
                                                <SettingCard>
                                                    <SectionHeader>
                                                        <SectionTitle>Renew Agreement Settings</SectionTitle>
                                                    </SectionHeader>

                                                    <SettingRow>
                                                        <Label>Agreement Renewal</Label>

                                                        <Value className="flex flex-col items-end text-right">

                                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full
  ${currentSettings.autoRenew
                                                                    ? "bg-green-100 text-green-700"
                                                                    : "bg-gray-100 text-gray-600"}
`}>
                                                                {currentSettings.autoRenew ? "Enabled" : "Manual"}
                                                            </span>

                                                            <span className="text-xs font-normal text-gray-500 mt-0.5">
                                                                {currentSettings.autoRenew
                                                                    ? "Agreements renew automatically on expiry"
                                                                    : "Requires manual renewal after expiry"}
                                                            </span>

                                                            {currentSettings.autoRenew && (
                                                                <span className="text-[11px] text-green-600 mt-1">
                                                                    ↑ Reduces manual work and ensures continuity
                                                                </span>
                                                            )}

                                                        </Value>
                                                    </SettingRow>
                                                    <SettingRow>
                                                        <Label>Increment Value</Label>
                                                        <Value>
                                                            {currentSettings.incrementType === 1
                                                                ? `₹${currentSettings.incrementValue || 0}`
                                                                : `${currentSettings.incrementValue || 0}%`
                                                            }
                                                        </Value>
                                                    </SettingRow>
                                                    <SettingRow>
                                                        <Label>Apply to Security Deposit</Label>
                                                        <Value>
                                                            {currentSettings.increaseSecurity ? 'Yes' : 'No'}
                                                        </Value>
                                                    </SettingRow>
                                                    <SettingRow>
                                                        <Label>Agreement Charges</Label>
                                                        <Value>
                                                            {currentSettings.agreementCharges
                                                                ? `₹${currentSettings.agreementCharges}`
                                                                : '₹0'
                                                            }
                                                        </Value>
                                                    </SettingRow>
                                                </SettingCard>

                                                {/* <ReplicateSection>
                                                    <ReplicateHeader>
                                                        <ReplicateTitle>Replicate Settings to Other Properties</ReplicateTitle>
                                                    </ReplicateHeader>

                                                    <ReplicateContent>
                                                        <PropertiesSelection>
                                                            <CategoryCard
                                                                type="button"
                                                                selectedCategory={selectedProperties.length > 0 ? {
                                                                    label: renderSelectedProperties(selectedProperties),
                                                                    value: "properties"
                                                                } : null}
                                                                title="Property or Properties"
                                                                onClick={() => setShowMultiSelectModal(true)}
                                                            />

                                                            <NewPackage
                                                                onClick={handleReplicateSettings}
                                                                disabled={isLoading || selectedProperties.length === 0 || isReplicating}
                                                            >
                                                                {isReplicating ? "Replicating..." : `Replicate to ${selectedProperties.length} Properties`}
                                                            </NewPackage>

                                                        </PropertiesSelection>
                                                    </ReplicateContent>
                                                </ReplicateSection> */}

                                                <ReplicateSection>

                                                    {/* Header */}
                                                    <ReplicateHeader>
                                                        <ReplicateTitle>
                                                            Apply Settings to Other Properties
                                                        </ReplicateTitle>
                                                        <ReplicateSubtitle>
                                                            Instantly copy these settings across selected properties
                                                        </ReplicateSubtitle>
                                                    </ReplicateHeader>

                                                    <ReplicateContent>

                                                        {/* Selection Card */}
                                                        <ReplicateSelectionCard
                                                            type="button"
                                                            onClick={() => setShowMultiSelectModal(true)}
                                                        >
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-700">
                                                                    Select Properties
                                                                </p>
                                                                <p className="text-xs text-gray-500 mt-0.5">
                                                                    {selectedProperties.length > 0
                                                                        ? renderSelectedProperties(selectedProperties)
                                                                        : "Choose one or more properties"}
                                                                </p>
                                                            </div>

                                                            <span className="text-primary-color text-sm font-medium">
                                                                {selectedProperties.length > 0 ? "Edit" : "Select"}
                                                            </span>
                                                        </ReplicateSelectionCard>

                                                        {/* CTA Button */}
                                                        <PrimaryAction
                                                            onClick={handleReplicateSettings}
                                                            disabled={isLoading || selectedProperties.length === 0 || isReplicating}
                                                        >
                                                            {isReplicating
                                                                ? "Applying..."
                                                                : selectedProperties.length > 0
                                                                    ? `Apply to ${selectedProperties.length} Properties`
                                                                    : "Select Properties First"}
                                                        </PrimaryAction>

                                                    </ReplicateContent>
                                                </ReplicateSection>

                                            </SettingsGrid>

                                            <ActionSection $isEditing={isEditing}>
                                                <SideCancelBtn
                                                    type="button"
                                                    onClick={onClose}
                                                >
                                                    Cancel
                                                </SideCancelBtn>
                                                <SubmitBtn $isEditing={isEditing}
                                                    onClick={() => setIsEditing(true)}>
                                                    Edit
                                                </SubmitBtn>
                                            </ActionSection>
                                        </>
                                    )}
                                </>
                            )}
                        </Section>
                    </div>

                    <SinglePropSelectModal
                        isOpen={showPropertyModal}
                        onClose={() => setShowPropertyModal(false)}
                        onSelect={handleSelectedProperty}
                        options={filteredProperties}
                        selectedProperty={selectedProperty}
                    />

                    <MultiPropSelectModal
                        isOpen={showMultiSelectModal}
                        onClose={() => setShowMultiSelectModal(false)}
                        onSelect={handleMultiPropertySave}
                        options={filteredProperties}
                        selectedProperties={selectedProperties}
                    />
                </ContentContainer>
            )}
        </Container>
    );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const Section = tw.div`space-y-6`;
const PropertySection = tw.div`bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4`;
// const SettingsGrid = tw.div`space-y-4`;
// const SettingCard = tw.div`bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200`;
// const SettingRow = tw.div`flex items-start justify-between space-x-6`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
// const Value = tw.span`text-gray-800 font-medium`;
// const SectionTitle = tw.h2`text-md font-semibold text-gray-800 mt-4 mx-4`;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const PropertiesSelection = tw.div`space-y-3`;
const NewPackage = tw.button`w-full border border-primary-color items-center mb-2 text-primary-color h-12 rounded-lg hover:bg-primary-color hover:text-white disabled:opacity-50 disabled:cursor-not-allowed`;


const SettingsGrid = tw.div`space-y-5`;

const SettingCard = tw.div`
  bg-white rounded-xl border border-gray-200 
  shadow-sm hover:shadow-md transition-all duration-200
`;

const SectionHeader = tw.div`
  px-5 pt-5 pb-3 border-b border-gray-100
`;

const SectionTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingRow = tw.div`
  flex items-center justify-between 
  px-5 py-3 border-b border-gray-100 last:border-0
`;

const Label = tw.span`
  text-sm text-gray-500
`;

const Value = tw.span`
  text-sm font-semibold text-gray-900
`;

const ImpactBox = tw.div`
  mt-2 text-[11px] text-gray-600 bg-gray-50 
  border border-gray-200 rounded-lg px-3 py-2
`;

const HelperText = tw.p`
  text-[11px] text-gray-400 mt-1
`;
export default AgreementRenew;